import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { AnimatePresence, animate, motion, useMotionValue } from 'framer-motion';
import {
  SHEET_DRAG_ENGAGE_PX,
  sheetGestureVerdict,
  shouldDismissSheet,
} from '@/utils/sheetGesture';
import { PULL_REFRESH_IGNORE_ATTR, pullRefreshIgnore } from '@/hooks/usePullToRefresh';

/**
 * Reusable bottom-sheet chrome: dimmed backdrop + slide-up panel with a
 * grabber and optional title. Tapping the backdrop closes it.
 *
 * B6 (2.1.0): swiping down **anywhere on the sheet** dismisses it, not just on
 * the grabber. The panel no longer scrolls itself — its content sits in an
 * inner scroller — so a pointer gesture can be arbitrated before the browser
 * commits to it: pull down with the content at the top and the sheet follows
 * the finger; scroll away from the top, or pull up, and the gesture is left to
 * native scrolling (see utils/sheetGesture). The grabber keeps working even
 * mid-scroll because a gesture starting outside the scroller ignores scrollTop.
 *
 * Why hand-rolled instead of framer's `drag="y"`: `drag` makes framer write
 * `touch-action: pan-x` onto the panel, and touch-action combines down the
 * ancestor chain — which would kill vertical scrolling for every child,
 * including the 600-emoji reaction picker. Driving a motion value ourselves
 * keeps touch-action per zone instead.
 *
 * android-sheet-swipe (2.6.2): leaving touch-action at `auto` everywhere broke
 * every dismiss on Android — Chromium takes a vertical touch over at the first
 * move past the slop and sends `pointercancel`, even when nothing can scroll.
 * So the grabber and the title are `touch-none` (nothing scrolls there), and
 * the scroller keeps `auto`: a scroll container resets the chain, so the list
 * still scrolls natively.
 */

// Hoisted so their identity is stable across renders: an inline object would
// restart the keyframes every time a parent re-renders while the sheet is open.
const HINT_ANIMATE = { y: [0, 5, 0, 5, 0] };
const HINT_TRANSITION = { delay: 0.5, duration: 0.9, ease: 'easeInOut' } as const;
// sheet-open-motion — a cubic-bezier TWEEN, not a spring: framer-motion hands a
// tween on transform/opacity to the Web Animations API (the compositor), which
// keeps animating while the main thread mounts the sheet. The spring was
// computed in JS frame by frame, and the WebView painted only its first and
// last state. iOS-like ease-out; closing is a little quicker than opening.
const SHEET_EASE = [0.32, 0.72, 0, 1] as [number, number, number, number];
export const SHEET_ENTER_TRANSITION = { type: 'tween', duration: 0.28, ease: SHEET_EASE } as const;
export const SHEET_EXIT_TRANSITION = { type: 'tween', duration: 0.2, ease: SHEET_EASE } as const;
const SNAP_BACK_TRANSITION = { type: 'spring', stiffness: 420, damping: 38 } as const;

/**
 * emoji-sheet-swipe — how far the content under the finger is scrolled: the
 * largest scrollTop from the touched element up to the sheet's scroller. The
 * content may hold its own list (the emoji picker's grid), and reading only
 * the sheet's scroller — always at 0 then — closed the sheet while the user
 * was scrolling that list back up.
 */
function scrollTopUnderFinger(touched: Element, scroller: Element): number {
  let max = 0;
  for (let el: Element | null = touched; el; el = el.parentElement) {
    max = Math.max(max, el.scrollTop);
    if (el === scroller) break;
  }
  return max;
}

/** The scrollTop that decides drag vs scroll for a gesture that started on `touched`: 0 outside the scroller (grabber, title always drag). */
function scrollTopAt(touched: Element | null, scroller: Element | null): number {
  return touched && scroller && scroller.contains(touched)
    ? scrollTopUnderFinger(touched, scroller)
    : 0;
}

const OVERLAY = `[${PULL_REFRESH_IGNORE_ATTR}]`;

/**
 * emoji-sheet-swipe — a sheet opened from inside this one (the circle emoji
 * picker in the create-circle sheet) owns its own gestures: events bubble up
 * the React tree, and one swipe used to close both sheets.
 */
function startsInNestedOverlay(target: Element, own: Element): boolean {
  return target.closest(OVERLAY) !== own.closest(OVERLAY);
}

type Gesture = {
  pointerId: number;
  startY: number;
  lastY: number;
  lastT: number;
  /** px per ms, signed — positive is downward. */
  velocity: number;
  /** Where the finger landed (see scrollTopAt). */
  touched: Element | null;
  /** Have we claimed the gesture as a sheet drag? */
  engaged: boolean;
};

export default function BottomSheet({
  open,
  title,
  titleAction,
  onClose,
  children,
  testid,
}: {
  open: boolean;
  title?: string;
  /** Optional control on the title row (e.g. a reset CTA). The row keeps a
   *  reserved height so the sheet does not jump when the action appears. */
  titleAction?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  testid?: string;
}) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const gesture = useRef<Gesture | null>(null);
  const dragY = useMotionValue(0);

  /**
   * MR-2.1.0 — `animate()` on an externally-owned motion value returns controls
   * and keeps its own rAF loop running; it is NOT tied to the component
   * lifecycle. Left uncaptured it produced two defects:
   *   1. unmounting mid-snap-back (navigating away while the sheet springs
   *      home) left the loop ticking on a detached value — a leak;
   *   2. two quick successive drags started two animations on the SAME value,
   *      which then fought each other frame by frame.
   * Holding the controls in a ref fixes both: stop the previous one before
   * starting the next, and stop whatever is running on unmount.
   */
  const snapBack = useRef<ReturnType<typeof animate> | null>(null);

  const startSnapBack = useCallback(() => {
    snapBack.current?.stop();
    snapBack.current = animate(dragY, 0, SNAP_BACK_TRANSITION);
  }, [dragY]);

  useEffect(() => () => snapBack.current?.stop(), []);

  // A dismissing swipe leaves the offset in place so the exit animation
  // continues from where the finger let go. Reset before the next open paints
  // (layout effect, not effect) so a reopened sheet never flashes mid-swipe.
  // Killing any in-flight spring first — otherwise it would keep writing to
  // dragY after the reset and re-introduce the mid-swipe flash.
  useLayoutEffect(() => {
    if (open) {
      snapBack.current?.stop();
      dragY.set(0);
    }
  }, [open, dragY]);

  /**
   * android-sheet-swipe — the content keeps `touch-action: auto` (its list
   * scrolls), so Chromium takes a downward touch over at the first move past
   * the slop and cancels the pointer drag. The only way to keep it is to
   * `preventDefault()` that first move, which React cannot do (its touch
   * listeners are passive): a native, non-passive listener decides on the
   * first move — same verdict as the pointer path, with no engage threshold
   * (Chromium only sends the first move once past its slop, and starts its own
   * scroll right after) — and keeps preventing until the finger goes back up.
   * Never on touchstart: taps and buttons stay.
   */
  const panelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const panel = panelRef.current;
    if (!open || !panel) return;
    let start: { x: number; y: number; target: Element | null } | null = null;
    let claimed: boolean | null = null;
    const onTouchStart = (e: TouchEvent) => {
      const { clientX: x, clientY: y } = e.touches[0];
      start = { x, y, target: e.target instanceof Element ? e.target : null };
      claimed = null;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!start) return;
      const dx = e.touches[0].clientX - start.x;
      const dy = e.touches[0].clientY - start.y;
      if (claimed === null) {
        if (dx === 0 && dy === 0) return;
        // Only a mostly vertical move can be a dismiss: a sideways swipe that
        // dips a pixel down belongs to a strip (picker categories, reaction tabs).
        claimed =
          Math.abs(dy) >= Math.abs(dx) &&
          !!start.target &&
          !startsInNestedOverlay(start.target, panel) &&
          sheetGestureVerdict(dy, scrollTopAt(start.target, scrollerRef.current), 0) === 'drag';
      } else if (claimed && dy < 0) {
        // The finger went back above where it started: the pointer path has
        // given up the drag, so hand the touch back to native scrolling.
        claimed = false;
      }
      if (claimed && e.cancelable) e.preventDefault();
    };
    panel.addEventListener('touchstart', onTouchStart, { passive: true });
    panel.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      panel.removeEventListener('touchstart', onTouchStart);
      panel.removeEventListener('touchmove', onTouchMove);
    };
  }, [open]);

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target instanceof Element && startsInNestedOverlay(e.target, e.currentTarget)) return;
    gesture.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      lastY: e.clientY,
      lastT: performance.now(),
      velocity: 0,
      touched: e.target instanceof Element ? e.target : null,
      engaged: false,
    };
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    if (!g || g.pointerId !== e.pointerId) return;
    const dy = e.clientY - g.startY;

    if (!g.engaged) {
      const verdict = sheetGestureVerdict(dy, scrollTopAt(g.touched, scrollerRef.current));
      if (verdict === 'wait') return;
      if (verdict === 'scroll') {
        gesture.current = null;
        return;
      }
      g.engaged = true;
      // Keep receiving moves even if the finger leaves the panel.
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }

    const now = performance.now();
    const dt = now - g.lastT;
    if (dt > 0) g.velocity = (e.clientY - g.lastY) / dt;
    g.lastY = e.clientY;
    g.lastT = now;
    // Never lift the sheet above its resting position, and subtract the engage
    // threshold so the panel starts moving from exactly under the finger.
    dragY.set(Math.max(0, dy - SHEET_DRAG_ENGAGE_PX));
  };

  const handlePointerEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    gesture.current = null;
    if (!g || g.pointerId !== e.pointerId || !g.engaged) return;
    const dy = Math.max(0, e.clientY - g.startY - SHEET_DRAG_ENGAGE_PX);
    if (shouldDismissSheet(dy, g.velocity)) {
      snapBack.current?.stop();
      onClose();
      return;
    }
    startSnapBack();
  };

  const handlePointerCancel = () => {
    const g = gesture.current;
    gesture.current = null;
    if (g?.engaged) startSnapBack();
  };

  return (
    <AnimatePresence>
      {open && (
        // sheet-open-motion — backdrop and panel are SIBLINGS. The panel used to
        // be a child of the fading backdrop, and opacity composes down the
        // tree: the whole sheet first showed as a ~15 % ghost over the screen.
        // Now only the backdrop fades; the panel is always opaque and slides.
        // The root is a motion element so AnimatePresence waits for both exits.
        <motion.div
          className="tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-end"
          data-testid={testid}
          {...pullRefreshIgnore}
        >
          <motion.div
            className="tw:absolute tw:inset-0 tw:bg-black/60"
            onClick={onClose}
            data-testid="sheet-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          {/* Outer wrapper owns the open/close slide so the drag offset below
              can be driven independently without fighting the transition. */}
          <motion.div
            className="tw:relative tw:w-full"
            initial={{ y: '100%' }}
            animate={{ y: 0, transition: SHEET_ENTER_TRANSITION }}
            exit={{ y: '100%', transition: SHEET_EXIT_TRANSITION }}
          >
            <motion.div
              ref={panelRef}
              data-testid="sheet-panel"
              className="tw:flex tw:max-h-[75vh] tw:w-full tw:flex-col tw:rounded-t-3xl tw:bg-dark-1 tw:p-4 tw:pb-0"
              style={{ y: dragY }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerEnd}
              onPointerCancel={handlePointerCancel}
            >
              {/* Grabber — still the obvious affordance, and still draggable
                  even when the content below is scrolled. */}
              <div
                data-testid="sheet-drag-handle"
                className="tw:-m-4 tw:mb-0 tw:shrink-0 tw:cursor-grab tw:touch-none tw:px-4 tw:pb-2 tw:pt-4"
              >
                <motion.div
                  className="tw:mx-auto tw:h-1 tw:w-10 tw:rounded-full tw:bg-grey-4/50"
                  // Swipe-down hint: two subtle downward nudges shortly after open.
                  animate={HINT_ANIMATE}
                  transition={HINT_TRANSITION}
                />
              </div>
              {title && (
                <div className="tw:mb-3 tw:mt-1 tw:flex tw:min-h-8 tw:shrink-0 tw:touch-none tw:items-center tw:justify-between tw:gap-3 tw:px-1">
                  <h2 className="tw:text-[15px] tw:font-semibold tw:text-white">{title}</h2>
                  {titleAction}
                </div>
              )}
              {/* The only scrolling element: its scrollTop is what tells a
                  dismiss swipe apart from a content scroll.
                  sheet-list-bottom — the bottom inset (breathing room + home
                  indicator) is padding INSIDE the scroller, not on the panel:
                  a long list then scrolls under it down to the sheet's edge
                  instead of being cut off ~60px above it. */}
              <div
                ref={scrollerRef}
                data-testid="sheet-scroll"
                className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:overscroll-contain"
                style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
              >
                {children}
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
